import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useProjectAuth } from './useProjectAuth'

const fmt  = (n, d=0) => n != null && n !== '' ? Number(n).toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d}) : '—'
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—'

const XIcon     = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
const PlusIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
const TrashIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t) }, [onClose])
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white ${type==='error'?'bg-red-600':'bg-[#1A7A4A]'}`}>
      {message}
    </div>
  )
}

const ConfirmOverlay = ({ message, onConfirm, onCancel, confirmLabel='Confirm', confirmColor='bg-red-600' }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black/40" onClick={onCancel}/>
    <div className="relative bg-white rounded-xl shadow-2xl p-6 w-80">
      <p className="text-sm text-[#1C1A18] mb-5">{message}</p>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg">Cancel</button>
        <button onClick={onConfirm} className={`px-4 py-2 text-sm text-white rounded-lg ${confirmColor}`}>{confirmLabel}</button>
      </div>
    </div>
  </div>
)

const STATUS_CONFIG = {
  under_discussion: { label: 'Under Discussion', color: '#6B7280', bg: '#F3F4F6', role: 'site_engineer' },
  quantified:       { label: 'Quantified',        color: '#D97706', bg: '#FEF3C7', role: 'procurement' },
  priced:           { label: 'Priced',             color: '#2563EB', bg: '#DBEAFE', role: 'cost_control' },
  approved:         { label: 'Approved',           color: '#15803D', bg: '#DCFCE7', role: null },
  rejected:         { label: 'Rejected',           color: '#DC2626', bg: '#FEE2E2', role: null },
}

const EMPTY_CHANGE = { title: '', description: '', approval_probability_pct: 50 }
const EMPTY_ITEM = { cost_code_id: '', sub_code: '', description: '', qty_impact: '', unit: '', approx_cost: '' }

export default function RisksChanges({ initProject }) {
  const { profile, role } = useAuth?.() || {}
  const [projects, setProjects]             = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  // Real authorization source -- can() mirrors has_permission() exactly.
  const { can } = useProjectAuth(selectedProject?.id)
  const [changes, setChanges]               = useState([])
  const [items, setItems]                   = useState([]) // all items for the project, keyed by change_id when needed
  const [costCodes, setCostCodes]           = useState([])
  const [subCodes, setSubCodes]             = useState([])
  const [loading, setLoading]               = useState(false)
  const [toast, setToast]                   = useState(null)
  const [confirmDelete, setConfirmDelete]   = useState(null)
  const [confirmDecision, setConfirmDecision] = useState(null) // {change, decision:'approved'|'rejected'}

  const [statusFilter, setStatusFilter]     = useState('all')

  const [showModal, setShowModal]           = useState(false)
  const [form, setForm]                     = useState({ ...EMPTY_CHANGE })

  const [expandedId, setExpandedId]         = useState(null)
  const [itemForm, setItemForm]             = useState({ ...EMPTY_ITEM })
  const [addingItemFor, setAddingItemFor]   = useState(null)

  const showToast = (msg, type='success') => setToast({ message: msg, type })

  useEffect(() => {
    supabase.from('projects').select('id,name').order('name').then(({ data }) => setProjects(data||[]))
  }, [])

  useEffect(() => { if (initProject) setSelectedProject(initProject) }, [initProject])

  useEffect(() => {
    if (selectedProject?.id) {
      loadChanges(selectedProject.id)
      loadCostCodes(selectedProject.id)
      loadSubCodes(selectedProject.id)
    }
  }, [selectedProject?.id]) // eslint-disable-line

  const loadChanges = useCallback(async (pid) => {
    setLoading(true)
    const { data: changesData } = await supabase.from('engineering_changes').select('*').eq('project_id', pid).order('created_at', { ascending: false })
    const ids = (changesData||[]).map(c => c.id)
    let itemsData = []
    if (ids.length > 0) {
      const { data } = await supabase.from('engineering_change_items').select('*').in('change_id', ids)
      itemsData = data || []
    }
    setChanges(changesData || [])
    setItems(itemsData)
    setLoading(false)
  }, [])

  const loadCostCodes = async (pid) => {
    const { data } = await supabase.from('cost_codes').select('id,code,description').eq('project_id', pid).eq('level', 1).eq('is_active', true).order('code')
    setCostCodes(data || [])
  }

  const loadSubCodes = async (pid) => {
    const { data } = await supabase.from('tender_direct_items')
      .select('budget_material_code, labour_code, budget_sc_code, budget_equipment_code, row_type, description, resource_description')
      .eq('project_id', pid).in('row_type', ['material', 'labour', 'subcontract', 'equipment'])
    const map = {}
    ;(data||[]).forEach(r => {
      let sc = null
      if (r.row_type === 'labour')           sc = r.labour_code
      else if (r.row_type === 'material')    sc = r.budget_material_code
      else if (r.row_type === 'subcontract') sc = r.budget_sc_code
      else if (r.row_type === 'equipment')   sc = r.budget_equipment_code
      if (!sc || map[sc]) return
      map[sc] = { sub_code: sc, description: r.resource_description || r.description || '' }
    })
    setSubCodes(Object.values(map).sort((a,b)=>a.sub_code.localeCompare(b.sub_code)))
  }

  // ── Create new change (Engineering step) ───────────────────
  const handleCreate = async () => {
    if (!form.title.trim()) return showToast('العنوان مطلوب', 'error')
    const payload = {
      project_id: selectedProject.id,
      title: form.title.trim(),
      description: form.description.trim() || null,
      approval_probability_pct: parseFloat(form.approval_probability_pct) || 0,
      status: 'under_discussion',
      created_by: profile?.full_name || null,
      qs_notified_at: new Date().toISOString(),
    }
    const { error } = await supabase.from('engineering_changes').insert(payload)
    if (error) return showToast('فشل: ' + error.message, 'error')
    showToast('تم إنشاء Change Notice — تم إشعار QS')
    setShowModal(false)
    setForm({ ...EMPTY_CHANGE })
    loadChanges(selectedProject.id)
  }

  // ── Update probability (Engineering can revise anytime) ─────
  const updateProbability = async (change, pct) => {
    await supabase.from('engineering_changes').update({ approval_probability_pct: pct }).eq('id', change.id)
    setChanges(prev => prev.map(c => c.id === change.id ? { ...c, approval_probability_pct: pct } : c))
  }

  // ── Add item (QS adds qty, Procurement adds approx_cost, Cost Control adds codes) ──
  const handleAddItem = async (changeId) => {
    if (!itemForm.description.trim()) return showToast('الوصف مطلوب', 'error')
    const payload = {
      change_id: changeId,
      cost_code_id: itemForm.cost_code_id || null,
      sub_code: itemForm.sub_code || null,
      description: itemForm.description.trim(),
      qty_impact: itemForm.qty_impact ? parseFloat(itemForm.qty_impact) : null,
      unit: itemForm.unit.trim() || null,
      approx_cost: itemForm.approx_cost ? parseFloat(itemForm.approx_cost) : null,
      added_by_role: role || null,
    }
    const { error } = await supabase.from('engineering_change_items').insert(payload)
    if (error) return showToast('فشل: ' + error.message, 'error')
    showToast('تمت الإضافة')
    setItemForm({ ...EMPTY_ITEM })
    setAddingItemFor(null)
    loadChanges(selectedProject.id)
  }

  const handleDeleteItem = async (itemId, changeId) => {
    await supabase.from('engineering_change_items').delete().eq('id', itemId)
    loadChanges(selectedProject.id)
  }

  // ── Advance status ────────────────────────────────────────
  const advanceStatus = async (change, newStatus) => {
    const patch = { status: newStatus }
    const now = new Date().toISOString()
    if (newStatus === 'quantified')  { patch.qs_completed_at = now; patch.procurement_notified_at = now }
    if (newStatus === 'priced')      { patch.procurement_completed_at = now }
    const { error } = await supabase.from('engineering_changes').update(patch).eq('id', change.id)
    if (error) return showToast('فشل: ' + error.message, 'error')
    showToast(newStatus === 'quantified' ? 'تم — الإشعار راح لـProcurement' : newStatus === 'priced' ? 'تم — جاهز لتكويد Cost Control والقرار النهائي' : 'تم التحديث')
    loadChanges(selectedProject.id)
  }

  // ── Final decision ────────────────────────────────────────
  const finalDecision = async (change, decision) => {
    const { error } = await supabase.from('engineering_changes').update({
      status: decision,
      final_decision_at: new Date().toISOString(),
      final_decision_by: profile?.full_name || null,
    }).eq('id', change.id)
    if (error) return showToast('فشل: ' + error.message, 'error')
    showToast(decision === 'approved' ? '✓ تم الاعتماد — التأثير هيتحسب 100% في الForecast' : '✗ تم الرفض — التأثير اتشال من الحسابات')
    setConfirmDecision(null)
    loadChanges(selectedProject.id)
  }

  const handleDelete = async (id) => {
    await supabase.from('engineering_changes').delete().eq('id', id)
    showToast('تم الحذف')
    setConfirmDelete(null)
    loadChanges(selectedProject.id)
  }

  const itemsFor = (changeId) => items.filter(i => i.change_id === changeId)
  const totalCostFor = (changeId) => itemsFor(changeId).reduce((s,i) => s + (parseFloat(i.approx_cost)||0), 0)
  const weightedCostFor = (change) => totalCostFor(change.id) * ((parseFloat(change.approval_probability_pct)||0)/100)

  const filtered = changes.filter(c => statusFilter === 'all' || c.status === statusFilter)

  // Summary
  const totalPending = changes.filter(c => !['approved','rejected'].includes(c.status))
  const totalWeightedImpact = totalPending.reduce((s,c) => s + weightedCostFor(c), 0)
  const totalApprovedImpact = changes.filter(c=>c.status==='approved').reduce((s,c) => s + totalCostFor(c.id), 0)

  return (
    <div className="min-h-screen bg-[#F9FAFB] p-6">
      {toast && <Toast {...toast} onClose={() => setToast(null)}/>}
      {confirmDelete && (
        <ConfirmOverlay message="متأكد من حذف الـChange ده؟" onConfirm={() => handleDelete(confirmDelete)} onCancel={() => setConfirmDelete(null)}/>
      )}
      {confirmDecision && (
        <ConfirmOverlay
          message={confirmDecision.decision === 'approved'
            ? `اعتماد "${confirmDecision.change.title}"؟ التأثير (${fmt(totalCostFor(confirmDecision.change.id))}) هيتحسب 100% في الForecast.`
            : `رفض "${confirmDecision.change.title}"؟ التأثير هيتشال بالكامل من أي حسابات.`}
          confirmLabel={confirmDecision.decision === 'approved' ? 'Approve' : 'Reject'}
          confirmColor={confirmDecision.decision === 'approved' ? 'bg-[#1A7A4A]' : 'bg-red-600'}
          onCancel={() => setConfirmDecision(null)}
          onConfirm={() => finalDecision(confirmDecision.change, confirmDecision.decision)}/>
      )}

      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold text-[#1C1A18]">Risks & Changes</h1>
            <p className="text-xs text-[#6B7280] mt-0.5">Engineering change notices — early warning workflow: Engineering → QS → Procurement → Decision.</p>
          </div>
          <select value={selectedProject?.id || ''} onChange={e => setSelectedProject(projects.find(p => p.id === e.target.value) || null)}
            className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">— Select Project —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {selectedProject && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <div className="text-xl font-extrabold text-[#6B7280]">{totalPending.length}</div>
                <div className="text-xs text-[#6B7280] mt-1">Open Changes — in progress</div>
              </div>
              <div className="bg-white rounded-xl border border-amber-200 p-4">
                <div className="text-xl font-extrabold text-amber-600">{fmt(totalWeightedImpact)}</div>
                <div className="text-xs text-[#6B7280] mt-1">Weighted Pending Impact (× probability)</div>
              </div>
              <div className="bg-white rounded-xl border border-emerald-200 p-4">
                <div className="text-xl font-extrabold text-emerald-600">{fmt(totalApprovedImpact)}</div>
                <div className="text-xs text-[#6B7280] mt-1">Approved Impact — confirmed 100%</div>
              </div>
            </div>

            {/* Toolbar */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 mb-4 flex flex-wrap items-center gap-3">
              <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-xs">
                <option value="all">All Status</option>
                {Object.entries(STATUS_CONFIG).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <div className="flex-1"/>
              {can('EngineeringChange.Create') && (
                <button onClick={() => { setForm({...EMPTY_CHANGE}); setShowModal(true) }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1B3A6B] text-white rounded-lg text-xs font-medium">
                  <PlusIcon/> New Change Notice
                </button>
              )}
            </div>

            {/* List */}
            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12 text-[#9CA3AF]">Loading...</div>
              ) : filtered.length === 0 ? (
                <div className="bg-white rounded-xl border border-[#E5E7EB] p-12 text-center text-[#9CA3AF]">لا يوجد تغييرات</div>
              ) : filtered.map(c => {
                const sc = STATUS_CONFIG[c.status]
                const cItems = itemsFor(c.id)
                const totalCost = totalCostFor(c.id)
                const weighted = weightedCostFor(c)
                const expanded = expandedId === c.id
                return (
                  <div key={c.id} className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                    <div className="p-4 cursor-pointer" onClick={() => setExpandedId(expanded ? null : c.id)}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span style={{background:sc.bg,color:sc.color,padding:'2px 10px',borderRadius:12,fontSize:10.5,fontWeight:700}}>{sc.label}</span>
                            <span className="font-semibold text-[#1C1A18] text-sm">{c.title}</span>
                          </div>
                          {c.description && <p className="text-xs text-[#6B7280] mt-1">{c.description}</p>}
                          <p className="text-[10px] text-[#9CA3AF] mt-1.5">Created {fmtDate(c.created_at)} by {c.created_by||'—'}</p>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-sm font-bold text-[#1C1A18]">{fmt(totalCost)}</div>
                          <div className="text-[10px] text-[#9CA3AF]">Total Impact</div>
                          {!['approved','rejected'].includes(c.status) && (
                            <>
                              <div className="text-sm font-bold text-amber-600 mt-1">{fmt(weighted)}</div>
                              <div className="text-[10px] text-[#9CA3AF]">Weighted ({c.approval_probability_pct}%)</div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {expanded && (
                      <div className="border-t border-[#F3F4F6] p-4 space-y-4" onClick={e=>e.stopPropagation()}>
                        {/* Probability slider (Engineering) */}
                        {!['approved','rejected'].includes(c.status) && (
                          <div className="flex items-center gap-3 bg-[#F9FAFB] rounded-lg p-3">
                            <span className="text-xs font-medium text-[#6B7280] whitespace-nowrap">Approval Probability:</span>
                            <input type="range" min="0" max="100" step="5" value={c.approval_probability_pct}
                              onChange={e => updateProbability(c, parseFloat(e.target.value))}
                              className="flex-1"/>
                            <span className="text-sm font-bold text-[#1B3A6B] w-12 text-right">{c.approval_probability_pct}%</span>
                          </div>
                        )}

                        {/* Workflow timeline */}
                        <div className="flex items-center gap-2 text-[10px] text-[#9CA3AF] flex-wrap">
                          <span className={c.qs_notified_at ? 'text-[#1A7A4A] font-medium' : ''}>📤 QS Notified {fmtDate(c.qs_notified_at)}</span>
                          <span>→</span>
                          <span className={c.qs_completed_at ? 'text-[#1A7A4A] font-medium' : ''}>📏 Quantified {fmtDate(c.qs_completed_at)}</span>
                          <span>→</span>
                          <span className={c.procurement_completed_at ? 'text-[#1A7A4A] font-medium' : ''}>💰 Priced {fmtDate(c.procurement_completed_at)}</span>
                          <span>→</span>
                          <span className={c.final_decision_at ? 'text-[#1A7A4A] font-medium' : ''}>✓ Decided {fmtDate(c.final_decision_at)}</span>
                        </div>

                        {/* Items table */}
                        <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                          <table className="w-full text-xs">
                            <thead className="bg-[#F9FAFB]">
                              <tr>
                                <th className="px-3 py-2 text-left text-[#6B7280] font-semibold">Description</th>
                                <th className="px-3 py-2 text-left text-[#6B7280] font-semibold">Cost Code</th>
                                <th className="px-3 py-2 text-left text-[#6B7280] font-semibold">Sub Code</th>
                                <th className="px-3 py-2 text-right text-[#6B7280] font-semibold">Qty Impact</th>
                                <th className="px-3 py-2 text-left text-[#6B7280] font-semibold">Unit</th>
                                <th className="px-3 py-2 text-right text-[#6B7280] font-semibold">Approx Cost</th>
                                <th className="px-3 py-2"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[#F3F4F6]">
                              {cItems.map(item => (
                                <tr key={item.id}>
                                  <td className="px-3 py-2">{item.description}</td>
                                  <td className="px-3 py-2 font-mono">{costCodes.find(cc=>cc.id===item.cost_code_id)?.code || '—'}</td>
                                  <td className="px-3 py-2 font-mono">{item.sub_code || '—'}</td>
                                  <td className="px-3 py-2 text-right">{item.qty_impact != null ? fmt(item.qty_impact,2) : '—'}</td>
                                  <td className="px-3 py-2">{item.unit || '—'}</td>
                                  <td className="px-3 py-2 text-right font-semibold">{item.approx_cost != null ? fmt(item.approx_cost) : '—'}</td>
                                  <td className="px-3 py-2">
                                    {can('EngineeringChange.Delete') && (
                                      <button onClick={() => handleDeleteItem(item.id, c.id)} className="text-red-400 hover:text-red-600"><TrashIcon/></button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                              {cItems.length === 0 && (
                                <tr><td colSpan={7} className="px-3 py-4 text-center text-[#9CA3AF]">لا يوجد بنود متأثرة بعد</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* Add item form */}
                        {!['approved','rejected'].includes(c.status) && (
                          addingItemFor === c.id ? (
                            <div className="bg-[#F9FAFB] rounded-lg p-3 space-y-2">
                              <input value={itemForm.description} onChange={e=>setItemForm(f=>({...f,description:e.target.value}))}
                                placeholder="Description (e.g. Additional steel reinforcement)"
                                className="w-full border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-xs"/>
                              <div className="grid grid-cols-4 gap-2">
                                <select value={itemForm.cost_code_id} onChange={e=>setItemForm(f=>({...f,cost_code_id:e.target.value}))}
                                  className="border border-[#E5E7EB] rounded-lg px-2 py-1.5 text-xs">
                                  <option value="">Cost Code</option>
                                  {costCodes.map(cc => <option key={cc.id} value={cc.id}>{cc.code}</option>)}
                                </select>
                                <select value={itemForm.sub_code} onChange={e=>setItemForm(f=>({...f,sub_code:e.target.value}))}
                                  className="border border-[#E5E7EB] rounded-lg px-2 py-1.5 text-xs font-mono">
                                  <option value="">Sub Code</option>
                                  {subCodes.map(s => <option key={s.sub_code} value={s.sub_code}>{s.sub_code}</option>)}
                                </select>
                                <input type="number" value={itemForm.qty_impact} onChange={e=>setItemForm(f=>({...f,qty_impact:e.target.value}))}
                                  placeholder="Qty" className="border border-[#E5E7EB] rounded-lg px-2 py-1.5 text-xs"/>
                                <input value={itemForm.unit} onChange={e=>setItemForm(f=>({...f,unit:e.target.value}))}
                                  placeholder="Unit" className="border border-[#E5E7EB] rounded-lg px-2 py-1.5 text-xs"/>
                              </div>
                              <input type="number" value={itemForm.approx_cost} onChange={e=>setItemForm(f=>({...f,approx_cost:e.target.value}))}
                                placeholder="Approx Cost (Procurement)" className="w-full border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-xs"/>
                              <div className="flex gap-2">
                                <button onClick={() => setAddingItemFor(null)} className="flex-1 py-1.5 border border-[#E5E7EB] rounded-lg text-xs">Cancel</button>
                                <button onClick={() => handleAddItem(c.id)} className="flex-1 py-1.5 bg-[#1B3A6B] text-white rounded-lg text-xs font-medium">Add Item</button>
                              </div>
                            </div>
                          ) : (
                            can('EngineeringChange.Create') && (
                              <button onClick={() => { setItemForm({...EMPTY_ITEM}); setAddingItemFor(c.id) }}
                                className="text-xs text-[#1B3A6B] font-medium flex items-center gap-1"><PlusIcon/> Add affected item</button>
                            )
                          )
                        )}

                        {/* Status actions */}
                        <div className="flex items-center gap-2 pt-2 border-t border-[#F3F4F6]">
                          {c.status === 'under_discussion' && (
                            <button onClick={() => advanceStatus(c, 'quantified')} disabled={cItems.length===0}
                              className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium disabled:opacity-40">
                              → Mark Quantified (QS done, notify Procurement)
                            </button>
                          )}
                          {c.status === 'quantified' && (
                            <button onClick={() => advanceStatus(c, 'priced')} disabled={!cItems.some(i=>i.approx_cost)}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium disabled:opacity-40">
                              → Mark Priced (Procurement done, ready for coding & decision)
                            </button>
                          )}
                          {c.status === 'priced' && (
                            <>
                              <button onClick={() => setConfirmDecision({change:c, decision:'approved'})}
                                className="px-3 py-1.5 bg-[#1A7A4A] text-white rounded-lg text-xs font-medium">✓ Approve</button>
                              <button onClick={() => setConfirmDecision({change:c, decision:'rejected'})}
                                className="px-3 py-1.5 border border-red-300 text-red-600 rounded-lg text-xs font-medium">✕ Reject</button>
                            </>
                          )}
                          <div className="flex-1"/>
                          {can('EngineeringChange.Delete') && (
                            <button onClick={() => setConfirmDelete(c.id)} className="text-red-400 hover:text-red-600"><TrashIcon/></button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* New Change Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h2 className="text-base font-bold text-[#1C1A18]">New Change Notice</h2>
              <button onClick={() => setShowModal(false)}><XIcon/></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Title *</label>
                <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))}
                  placeholder="e.g. Concrete mix design change — Zone C"
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Description</label>
                <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}
                  rows={3} placeholder="Details from the meeting with the client..."
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Approval Probability (%)</label>
                <div className="flex items-center gap-3">
                  <input type="range" min="0" max="100" step="5" value={form.approval_probability_pct}
                    onChange={e=>setForm(f=>({...f,approval_probability_pct:e.target.value}))} className="flex-1"/>
                  <span className="text-sm font-bold text-[#1B3A6B] w-12 text-right">{form.approval_probability_pct}%</span>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-xs text-blue-700">
                بعد الحفظ، هيتحول الـstatus لـ"Under Discussion" وهيتبعت إشعار لـQS Engineer عشان يحدد الكميات والبنود المتأثرة.
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm">Cancel</button>
              <button onClick={handleCreate} className="flex-1 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium">Create & Notify QS</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
